import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export interface PosCurrencyRate {
    id: number;
    targetCurrency: string;
    baseCurrency: string;
    rate: string;
    buyRate: string | null;
    sellRate: string | null;
    source: string;
    isManualOverride: boolean;
    scrapedAt: string;
    expiresAt: string;
}

export interface DisplayCurrency {
    code: string;
    label: string;
    symbol: string;
}

@Injectable({ providedIn: 'root' })
export class CurrencyService {
    private readonly http = inject(HttpClient);

    readonly baseCurrency = signal<DisplayCurrency>({ code: 'CAD', label: 'Dollar canadien', symbol: '$' });
    readonly selectedCurrency = signal<DisplayCurrency | null>(null);
    readonly availableRates = signal<PosCurrencyRate[]>([]);

    readonly activeCurrency = computed(() => this.selectedCurrency() ?? this.baseCurrency());

    readonly exchangeRate = computed(() => {
        const selected = this.selectedCurrency();
        const base = this.baseCurrency();
        if (!selected || selected.code === base.code) return 1;
        const rateEntry = this.availableRates().find(r => r.targetCurrency === selected.code);
        if (!rateEntry) return 1;
        return 1 / Number(rateEntry.rate);
    });

    readonly exchangeRateDisplay = computed(() => {
        const selected = this.selectedCurrency();
        const base = this.baseCurrency();
        if (!selected || selected.code === base.code) return null;
        const rateEntry = this.availableRates().find(r => r.targetCurrency === selected.code);
        if (!rateEntry) return null;
        return {
            label: `1 ${selected.code} = ${Number(rateEntry.rate).toFixed(2)} ${base.code}`,
            source: rateEntry.source,
            isManual: rateEntry.isManualOverride,
            updatedAt: rateEntry.scrapedAt,
        };
    });

    convert(htgAmount: number): number {
        const rate = this.exchangeRate();
        if (rate === 1) return htgAmount;
        return htgAmount * rate;
    }

    setBase(currency: DisplayCurrency) {
        this.baseCurrency.set(currency);
    }

    setAvailableRates(rates: PosCurrencyRate[]) {
        this.availableRates.set(rates);
    }

    selectCurrency(currency: DisplayCurrency | null) {
        this.selectedCurrency.set(currency);
    }

    getAvailableCurrencies(): DisplayCurrency[] {
        const rates = this.availableRates();
        const base = this.baseCurrency();
        const fromRates: DisplayCurrency[] = rates.map(r => ({
            code: r.targetCurrency,
            label: r.targetCurrency,
            symbol: r.targetCurrency,
        }));
        return [base, ...fromRates];
    }
}
